import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Track } from '../models/Track';
import { Element } from '../models/Element';
import { TimelineElement } from './TimelineElement';

interface TimelineTrackProps {
  track: Track;
  timelineScale: number;
  selectedElementIds: string[];
  hideLabel?: boolean; // Added prop to hide the label
  onElementSelect: (elementId: string, e?: React.MouseEvent<HTMLDivElement>) => void;
  onMoveElement: (elementId: string, newStartTime: number) => void;
  onTrimElementStart: (elementId: string, newStartTime: number) => void;
  onTrimElementEnd: (elementId: string, newEndTime: number) => void;
}

export const TimelineTrack = observer(
  ({
    track,
    timelineScale,
    selectedElementIds,
    hideLabel = false,
    onElementSelect,
    onMoveElement,
    onTrimElementStart,
    onTrimElementEnd,
  }: TimelineTrackProps) => {
    // Generate track background color based on type
    const getTrackColor = () => {
      switch (track.type) {
        case 'video':
          return '#006FEE';
        case 'text':
          return '#CBD7F0';
        case 'audio':
          return '#7828C8';
        case 'image':
          return '#17C964';
        default:
          return '#333333';
      }
    };

    // Handle click with multi-select support
    const handleElementClick = (elementId: string, e: React.MouseEvent<HTMLDivElement>) => {
      // Don't stop propagation - we want the event to bubble up for playhead positioning
      console.log(
        `TimelineTrack: Element clicked ${elementId}, shiftKey: ${e.shiftKey}`,
      );

      // Get multiSelect state from shift key
      const isMultiSelect = e.shiftKey;

      // Call the parent's onElementSelect with the element ID and the click event
      onElementSelect(elementId, e);
    };

    // Get track icon and label
    const getTrackInfo = () => {
      switch (track.type) {
        case 'video':
          return { label: 'Video', icon: '🎬' };
        case 'text':
          return { label: 'Text', icon: 'T' };
        case 'audio':
          return { label: 'Audio', icon: '🔊' };
        case 'image':
          return { label: 'Image', icon: '🖼️' };
        default:
          return { label: track.type, icon: '◆' };
      }
    };

    const trackInfo = getTrackInfo();
    const trackColor = getTrackColor();

    return (
      <TrackContainer>
        {/* Only show the track header if hideLabel is false */}
        {!hideLabel && (
          <TrackHeader backgroundColor={trackColor}>
            <TrackIcon>{trackInfo.icon}</TrackIcon>
            <TrackLabel>{trackInfo.label}</TrackLabel>
          </TrackHeader>
        )}
        <TrackContent>
          {track.elements.map((element: Element) => (
            <TimelineElement
              key={element.id}
              element={element}
              timelineScale={timelineScale}
              isSelected={selectedElementIds.includes(element.id)}
              onClick={(e) => handleElementClick(element.id, e)}
              onMoveElement={onMoveElement}
              onTrimElementStart={onTrimElementStart}
              onTrimElementEnd={onTrimElementEnd}
            />
          ))}
        </TrackContent>
      </TrackContainer>
    );
  },
);

const TrackContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  border-bottom: 1px solid #333;
`;

const TrackHeader = styled.div<{ backgroundColor: string }>`
  display: flex;
  align-items: center;
  width: 120px;
  height: 100%;
  padding: 0 8px;
  background-color: #171724;
  border-right: 1px solid #333;
`;

const TrackIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  border-radius: 12px;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.1);
`;

const TrackLabel = styled.div`
  color: #f3e9d7;
  font-size: 12px;
  font-weight: 500;
`;

const TrackContent = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
`;
